import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import AuthFrame from "@/components/AuthFrame";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ name: "", email: params.get("email") || "", password: "" });
  const [loading, setLoading] = useState(false);
  const [mailSent, setMailSent] = useState(false);

  const onChange = (key) => (e) => {
    setMailSent(false);
    setForm({ ...form, [key]: e.target.value });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form.name, form.email, form.password);
      setMailSent(true);
      toast.success("Controleer je mail");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Aanmelden mislukt");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthFrame
      title="Account aanmaken"
      subtitle="Maak je account aan en bevestig daarna je e-mailadres."
      heroTitle="Je bijenwerk blijft bewaard."
      heroText="Standen, kasten en inspecties staan veilig op je account, ook als je later een nieuwe telefoon gebruikt."
      footer={
        <>
          Al een account?{" "}
          <Link to="/login" className="font-semibold text-amber-700 hover:text-amber-800" data-testid="link-login">
            Inloggen
          </Link>
          <span className="mx-2 text-zinc-300">·</span>
          <Link to="/welcome" className="font-semibold text-zinc-600 hover:text-zinc-800">Terug</Link>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="name" className="text-sm font-semibold text-zinc-800">Naam</Label>
            <Input
              id="name"
              data-testid="register-name"
              value={form.name}
              onChange={onChange("name")}
              className="h-[52px] rounded-xl text-base bg-white border-zinc-200 shadow-sm"
              autoComplete="name"
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="email" className="text-sm font-semibold text-zinc-800">E-mailadres</Label>
            <Input
              id="email"
              type="email"
              data-testid="register-email"
              value={form.email}
              onChange={onChange("email")}
              className="h-[52px] rounded-xl text-base bg-white border-zinc-200 shadow-sm"
              autoComplete="email"
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password" className="text-sm font-semibold text-zinc-800">Wachtwoord</Label>
            <Input
              id="password"
              type="password"
              data-testid="register-password"
              value={form.password}
              onChange={onChange("password")}
              className="h-[52px] rounded-xl text-base bg-white border-zinc-200 shadow-sm"
              autoComplete="new-password"
              required
              minLength={8}
              maxLength={128}
            />
          </div>

          {mailSent && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
              <div className="text-sm font-bold text-amber-950">Controleer je mail</div>
              <div className="text-xs text-amber-800/80 mt-0.5">
                Als dit e-mailadres gebruikt kan worden, sturen we een link om verder te gaan.
              </div>
            </div>
          )}

          <Button type="submit" disabled={loading} data-testid="register-submit" className="w-full h-[52px] rounded-xl text-base bg-amber-600 hover:bg-amber-700 font-bold btn-press shadow-sm shadow-amber-600/20">
            {loading ? "Bezig..." : "Account aanmaken"}
          </Button>
        </form>
    </AuthFrame>
  );
}
